/**
 * CuratedWeekendPage — public editorial inspiration page.
 * Composes real Farmly entities into a calm, narrative flow.
 *
 * Route: /inspiration/:slug
 * SoT: docs/curated-pages-public.md
 */
import { useParams, Navigate, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bed, Users, ArrowRight, MapPin, Clock, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Seo } from "@/components/common/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import { useProductPreviewImage } from "@/hooks/useProductPreviewImage";
import { resolveAssetUrl } from "@/lib/media/mediaApi";
import { track } from "@/lib/analytics";
import { useTrackOnce } from "@/lib/analytics/useTrackOnce";
import { activityDetailPath, producerProductPath, producerPublicPath } from "@/lib/paths";
import { useCuratedPage, type CuratedPageItem, type CuratedPageData } from "@/hooks/useCuratedPage";
import { useShare } from "@/hooks/useShare";

import { Card, CardContent } from "@/components/ui/card";
import { stripHtmlToText } from "@/lib/text";
import type { TimingMode } from "@/lib/events/eventCardUtils";
import { useMemo, useCallback } from "react";

/* ─── Helpers ───────────────────────────────────────── */

function trackItemClick(pageSlug: string, item: CuratedPageItem, position: number) {
  track("curated_item_clicked", {
    curated_page_slug: pageSlug,
    entity_type: item.entity_type,
    entity_id: item.entity_id,
    item_position: position,
  });
}

/** Group items by day_label, preserving order */
function groupByDay(items: CuratedPageItem[]): { dayLabel: string | null; items: CuratedPageItem[] }[] {
  const groups: { dayLabel: string | null; items: CuratedPageItem[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.dayLabel === item.day_label) {
      last.items.push(item);
    } else {
      groups.push({ dayLabel: item.day_label, items: [item] });
    }
  }
  return groups;
}

const CTA_LABELS: Record<string, string> = {
  accommodation: "Boka boende",
  event: "Se tider",
  product: "Se produkter",
  producer: "Besök gården",
  place: "Utforska platsen",
  area: "Utforska området",
};

function defaultCtaLabel(entityType: string, ctaLabel: string | null): string {
  return ctaLabel || CTA_LABELS[entityType] || "Läs mer";
}

/* ─── Data hooks ────────────────────────────────────── */

function useAccommodation(id: string | null) {
  return useQuery({
    queryKey: ["curated-accommodation", id],
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("producer_accommodations")
        .select("id, title, description_short, beds, guest_capacity, price_per_night_text, producer_id")
        .eq("id", id!)
        .eq("status", "public")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useEvent(id: string | null) {
  return useQuery({
    queryKey: ["curated-event", id],
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("producer_events")
        .select("id, title, start_date, end_date, timing_mode, event_type, producer_id")
        .eq("id", id!)
        .eq("status", "public")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useProduct(id: string | null) {
  return useQuery({
    queryKey: ["curated-product", id],
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("producer_products")
        .select("id, title, description, price_ore, currency, slug, status, producer_id")
        .eq("id", id!)
        .eq("status", "active")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useProducerSlug(producerId: string | null) {
  return useQuery({
    queryKey: ["curated-producer-slug", producerId],
    enabled: !!producerId,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("producers")
        .select("id, slug, name")
        .eq("id", producerId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useProducerEntity(id: string | null) {
  return useQuery({
    queryKey: ["curated-producer-entity", id],
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("producers")
        .select("id, name, slug, short_description, city, region, status")
        .eq("id", id!)
        .eq("status", "public")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function usePlaceEntity(id: string | null) {
  return useQuery({
    queryKey: ["curated-place-entity", id],
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("places")
        .select("id, slug, name, municipality, landskap, description_short")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useAreaEntity(id: string | null) {
  return useQuery({
    queryKey: ["curated-area-entity", id],
    enabled: !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas")
        .select("id, slug, name, description_short, municipality, landskap")
        .eq("id", id!)
        .eq("status", "public")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/** Resolve hero image URL from hero_media_asset_id */
function useHeroImageUrl(assetId: string | null) {
  return useQuery({
    queryKey: ["curated-hero-asset", assetId],
    enabled: !!assetId,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("media_assets")
        .select("id, bucket, path_320, path_640, path_1280")
        .eq("id", assetId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return resolveAssetUrl(data as any, 1280);
    },
  });
}

/* ─── Stop Cards ────────────────────────────────────── */

/** Shared stop card wrapper with editorial narrative */
function StopCardWrapper({
  item,
  children,
  className,
}: {
  item: CuratedPageItem;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {/* Narrative text above the card */}
      <div className="space-y-1">
        <h3 className="font-display text-lg md:text-xl font-medium text-foreground leading-snug">
          {item.narrative_title}
        </h3>
        {item.narrative_body && (
          <p className="text-[15px] text-muted-foreground/70 leading-relaxed max-w-[52ch]">
            {item.narrative_body}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

/** Generic entity card shell */
function EntityCard({
  imageUrl,
  imageAlt,
  fallbackIcon,
  aspectRatio = "16/9",
  children,
  href,
  onClick,
}: {
  imageUrl: string | null;
  imageAlt: string;
  fallbackIcon?: React.ReactNode;
  aspectRatio?: string;
  children: React.ReactNode;
  href: string;
  onClick?: () => void;
}) {
  return (
    <Link to={href} className="block group" onClick={onClick}>
      <Card className="overflow-hidden rounded-xl border-border/40 shadow-none hover:shadow-sm transition-shadow">
        {imageUrl ? (
          <div className={`aspect-[${aspectRatio}] overflow-hidden`} style={{ aspectRatio }}>
            <img
              src={imageUrl}
              alt={imageAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div
            className="bg-muted flex items-center justify-center"
            style={{ aspectRatio }}
          >
            {fallbackIcon || (
              <span className="text-2xl font-medium text-muted-foreground/40">
                {(imageAlt[0] ?? "?").toUpperCase()}
              </span>
            )}
          </div>
        )}
        <CardContent className="pt-3 pb-3 px-4 space-y-1.5">
          {children}
        </CardContent>
      </Card>
    </Link>
  );
}

function AccommodationStop({ item, pageSlug, position }: StopProps) {
  const { data: acc, isLoading } = useAccommodation(item.entity_id);
  const coverUrl = useCoverImageUrl("accommodation", acc?.id ?? null, 640);

  if (isLoading) return <StopSkeleton />;
  if (!acc) return null;

  const priceLabel = acc.price_per_night_text
    ? /kr|per|natt/i.test(acc.price_per_night_text)
      ? acc.price_per_night_text
      : `Från ${acc.price_per_night_text} kr per natt`
    : null;

  return (
    <StopCardWrapper item={item}>
      <EntityCard
        imageUrl={coverUrl}
        imageAlt={acc.title}
        fallbackIcon={<Bed className="h-8 w-8 text-muted-foreground/40" />}
        href={`/accommodation/${acc.id}`}
        onClick={() => trackItemClick(pageSlug, item, position)}
      >
        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
          {acc.title}
        </h4>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {acc.beds != null && acc.beds > 0 && (
            <span className="flex items-center gap-1">
              <Bed className="h-3 w-3" /> {acc.beds} bäddar
            </span>
          )}
          {acc.guest_capacity != null && acc.guest_capacity > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" /> Max {acc.guest_capacity}
            </span>
          )}
        </div>
        {acc.description_short && (
          <p className="text-sm text-muted-foreground line-clamp-2">{acc.description_short}</p>
        )}
        {priceLabel && <p className="text-sm font-medium text-foreground">{priceLabel}</p>}
        <StopCta label={defaultCtaLabel("accommodation", item.cta_label)} />
      </EntityCard>
    </StopCardWrapper>
  );
}

function EventStop({ item, pageSlug, position }: StopProps) {
  const { data: evt, isLoading } = useEvent(item.entity_id);
  const coverUrl = useCoverImageUrl("event", evt?.id ?? null, 640);
  const producerCoverUrl = useCoverImageUrl("producer", evt?.producer_id ?? null, 640);
  const finalCover = coverUrl || producerCoverUrl;

  if (isLoading) return <StopSkeleton />;
  if (!evt) return null;

  return (
    <StopCardWrapper item={item}>
      <EntityCard
        imageUrl={finalCover}
        imageAlt={evt.title}
        href={activityDetailPath(evt.id)}
        onClick={() => trackItemClick(pageSlug, item, position)}
      >
        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
          {evt.title}
        </h4>
        <StopCta label={defaultCtaLabel("event", item.cta_label)} />
      </EntityCard>
    </StopCardWrapper>
  );
}

function ProductStop({ item, pageSlug, position }: StopProps) {
  const { data: product, isLoading } = useProduct(item.entity_id);
  const { data: producer } = useProducerSlug(product?.producer_id ?? null);
  const { imageUrl: coverUrl } = useProductPreviewImage(product?.id ?? null);

  if (isLoading) return <StopSkeleton />;
  if (!product) return null;

  const productUrl = producerProductPath(
    producer?.slug || producer?.id || "",
    product.slug || product.id,
  );
  const descText = product.description ? stripHtmlToText(product.description) : null;

  return (
    <StopCardWrapper item={item}>
      <EntityCard
        imageUrl={coverUrl}
        imageAlt={product.title}
        aspectRatio="3/2"
        href={productUrl}
        onClick={() => trackItemClick(pageSlug, item, position)}
      >
        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
          {product.title}
        </h4>
        {producer?.name && (
          <p className="text-xs text-muted-foreground">{producer.name}</p>
        )}
        {descText && (
          <p className="text-sm text-muted-foreground line-clamp-2">{descText}</p>
        )}
        {product.price_ore != null && product.price_ore > 0 && (
          <p className="text-sm font-medium text-foreground">
            {new Intl.NumberFormat("sv-SE", {
              style: "currency",
              currency: product.currency || "SEK",
              minimumFractionDigits: 0,
            }).format(product.price_ore / 100)}
          </p>
        )}
        <StopCta label={defaultCtaLabel("product", item.cta_label)} />
      </EntityCard>
    </StopCardWrapper>
  );
}

function ProducerStop({ item, pageSlug, position }: StopProps) {
  const { data: producer, isLoading } = useProducerEntity(item.entity_id);
  const coverUrl = useCoverImageUrl("producer", producer?.id ?? null, 640);

  if (isLoading) return <StopSkeleton />;
  if (!producer) return null;

  const subtitle = [producer.city, producer.region].filter(Boolean).join(", ");

  return (
    <StopCardWrapper item={item}>
      <EntityCard
        imageUrl={coverUrl}
        imageAlt={producer.name}
        href={producerPublicPath(producer.slug || producer.id)}
        onClick={() => trackItemClick(pageSlug, item, position)}
      >
        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
          {producer.name}
        </h4>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        {producer.short_description && (
          <p className="text-sm text-muted-foreground line-clamp-2">{producer.short_description}</p>
        )}
        <StopCta label={defaultCtaLabel("producer", item.cta_label)} />
      </EntityCard>
    </StopCardWrapper>
  );
}

function PlaceStop({ item, pageSlug, position }: StopProps) {
  const { data: place, isLoading } = usePlaceEntity(item.entity_id);
  const coverUrl = useCoverImageUrl("place", place?.id ?? null, 640);

  if (isLoading) return <StopSkeleton />;
  if (!place) return null;

  const subtitle = [place.municipality, place.landskap].filter(Boolean).join(", ");

  return (
    <StopCardWrapper item={item}>
      <EntityCard
        imageUrl={coverUrl}
        imageAlt={place.name}
        href={`/place/${place.slug || place.id}`}
        onClick={() => trackItemClick(pageSlug, item, position)}
      >
        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
          {place.name}
        </h4>
        {subtitle && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {subtitle}
          </p>
        )}
        {place.description_short && (
          <p className="text-sm text-muted-foreground line-clamp-2">{place.description_short}</p>
        )}
        <StopCta label={defaultCtaLabel("place", item.cta_label)} />
      </EntityCard>
    </StopCardWrapper>
  );
}

function AreaStop({ item, pageSlug, position }: StopProps) {
  const { data: area, isLoading } = useAreaEntity(item.entity_id);
  const coverUrl = useCoverImageUrl("area", area?.id ?? null, 640);

  if (isLoading) return <StopSkeleton />;
  if (!area) return null;

  return (
    <StopCardWrapper item={item}>
      <EntityCard
        imageUrl={coverUrl}
        imageAlt={area.name}
        href={`/utforska/${area.slug}`}
        onClick={() => trackItemClick(pageSlug, item, position)}
      >
        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
          {area.name}
        </h4>
        {area.description_short && (
          <p className="text-sm text-muted-foreground line-clamp-2">{area.description_short}</p>
        )}
        <StopCta label={defaultCtaLabel("area", item.cta_label)} />
      </EntityCard>
    </StopCardWrapper>
  );
}

/* ─── Shared sub-components ─────────────────────────── */

interface StopProps {
  item: CuratedPageItem;
  pageSlug: string;
  position: number;
}

function StopCta({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary pt-1 group-hover:gap-1.5 transition-all">
      {label} <ArrowRight className="h-3.5 w-3.5" />
    </span>
  );
}

function StopSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}

function StopRenderer({ item, pageSlug, position }: StopProps) {
  switch (item.entity_type) {
    case "accommodation":
      return <AccommodationStop item={item} pageSlug={pageSlug} position={position} />;
    case "event":
      return <EventStop item={item} pageSlug={pageSlug} position={position} />;
    case "product":
      return <ProductStop item={item} pageSlug={pageSlug} position={position} />;
    case "producer":
      return <ProducerStop item={item} pageSlug={pageSlug} position={position} />;
    case "place":
      return <PlaceStop item={item} pageSlug={pageSlug} position={position} />;
    case "area":
      return <AreaStop item={item} pageSlug={pageSlug} position={position} />;
    default:
      return null;
  }
}

/* ─── Day Group ─────────────────────────────────────── */

function DayGroup({
  dayLabel,
  items,
  pageSlug,
  startIndex,
}: {
  dayLabel: string | null;
  items: CuratedPageItem[];
  pageSlug: string;
  startIndex: number;
}) {
  return (
    <div className="space-y-10 md:space-y-14">
      {dayLabel && (
        <div className="flex items-center gap-4 pt-2">
          <span className="text-sm font-medium text-foreground/70 tracking-wide">
            {dayLabel}
          </span>
          <div className="flex-1 h-px bg-border/30" />
        </div>
      )}
      {items.map((item, idx) => (
        <StopRenderer
          key={item.id}
          item={item}
          pageSlug={pageSlug}
          position={startIndex + idx}
        />
      ))}
    </div>
  );
}

/* ─── Hero ──────────────────────────────────────────── */

function CuratedHero({
  page,
  heroImageUrl,
}: {
  page: CuratedPageData;
  heroImageUrl: string | null;
}) {
  const { share } = useShare();

  const handleShare = useCallback(() => {
    share(
      { kind: "inspiration", slug: page.slug, name: page.title },
      "curated_hero",
    );
  }, [share, page.slug, page.title]);

  return (
    <section className="relative">
      {/* Hero image — only rendered when we have an actual image */}
      {heroImageUrl && (
        <div className="mx-auto max-w-[900px] px-5 sm:px-6 pt-6 md:pt-12">
          <div className="aspect-[21/9] sm:aspect-[2.4/1] rounded-2xl overflow-hidden">
            <img
              src={heroImageUrl}
              alt={page.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      <div className={cn(
        "mx-auto max-w-[820px] px-5 sm:px-6 pb-8 md:pb-14 text-center",
        heroImageUrl ? "pt-8 md:pt-12 space-y-5" : "pt-12 md:pt-20 space-y-5",
      )}>
        {/* Kicker — subtle, not heavy */}
        <p className="text-[10px] tracking-[0.2em] text-muted-foreground/50 font-medium uppercase">
          Inspiration
        </p>

        {/* Title */}
        <h1 className="font-display text-3xl md:text-[2.75rem] md:leading-[1.12] font-semibold text-foreground">
          {page.title}
        </h1>

        {/* Duration chip + share */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {page.duration_label && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted/50 rounded-full px-3 py-1">
              <Clock className="h-3 w-3" />
              {page.duration_label}
            </span>
          )}
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground/60 hover:text-foreground rounded-full px-3 py-1 transition-colors"
            aria-label={`Dela ${page.title}`}
          >
            <Share2 className="h-3.5 w-3.5" />
            Dela
          </button>
        </div>

        {/* Intro */}
        <p className="text-base md:text-[1.125rem] text-muted-foreground/80 max-w-[44ch] mx-auto leading-[1.75]">
          {page.intro}
        </p>
      </div>
    </section>
  );
}

/* ─── Outro ─────────────────────────────────────────── */

function CuratedOutro({ page }: { page: CuratedPageData }) {
  const { share } = useShare();

  const handleShare = useCallback(() => {
    share(
      { kind: "inspiration", slug: page.slug, name: page.title },
      "curated_outro",
    );
  }, [share, page.slug, page.title]);

  return (
    <section className="mx-auto max-w-[820px] px-5 sm:px-6 pt-8 md:pt-12 pb-16 md:pb-24 text-center space-y-6">
      <div className="w-12 h-px bg-border/40 mx-auto" />
      <p className="text-base md:text-lg text-muted-foreground/70 max-w-[40ch] mx-auto leading-[1.75] italic">
        När resan närmar sig sitt slut har tempot saktat ner – och något av upplevelsen följer med hem.
      </p>
      <div className="flex flex-col items-center gap-2 pt-2">
        <button
          onClick={handleShare}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground/60 hover:text-foreground transition-colors"
          aria-label={`Dela ${page.title}`}
        >
          <Share2 className="h-4 w-4" />
          Tipsa någon du vill åka hit med
        </button>
      </div>
    </section>
  );
}

/* ─── Page ──────────────────────────────────────────── */

export default function CuratedWeekendPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: page, isLoading } = useCuratedPage(slug);
  const { data: heroAssetUrl } = useHeroImageUrl(page?.hero_media_asset_id ?? null);

  // Fallback: first stop's cover image when no dedicated hero
  const firstItem = page?.items[0];
  const firstItemCoverUrl = useCoverImageUrl(
    (firstItem?.entity_type as any) || "place",
    firstItem?.entity_id ?? null,
    1280,
  );

  // Hero resolution chain: dedicated hero → first stop cover → null (collapse)
  const heroImageUrl = heroAssetUrl || firstItemCoverUrl || null;
  const ogImage = heroImageUrl;

  useTrackOnce("curated_page_viewed", { page_slug: slug ?? "" }, slug);

  // Day groups
  const dayGroups = useMemo(
    () => (page ? groupByDay(page.items) : []),
    [page],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <SiteHeader />
        <main className="flex-1 mx-auto max-w-[820px] px-5 sm:px-6 pt-14 space-y-6">
          <Skeleton className="h-8 w-2/3 mx-auto" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (!page) {
    return <Navigate to="/" replace />;
  }

  // Compute start indices for position tracking
  let runningIndex = 0;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Seo
        title={page.title}
        description={page.og_description || page.intro}
        image={ogImage}
        ogType="article"
        canonicalUrl={`${window.location.origin}/inspiration/${page.slug}`}
      />
      <SiteHeader />

      <main className="flex-1">
        <CuratedHero page={page} heroImageUrl={heroImageUrl} />

        {/* Stops flow */}
        <div className="mx-auto max-w-[820px] px-5 sm:px-6 space-y-16 md:space-y-24">
          {dayGroups.map((group, gi) => {
            const startIdx = runningIndex;
            runningIndex += group.items.length;
            return (
              <DayGroup
                key={gi}
                dayLabel={group.dayLabel}
                items={group.items}
                pageSlug={page.slug}
                startIndex={startIdx}
              />
            );
          })}
        </div>

        {/* Outro */}
        <CuratedOutro page={page} />
      </main>

      <SiteFooter />
    </div>
  );
}
